/**
 * @file planCompare.ts
 * @brief "Compare all plans" rows from the server's plan catalogue
 *        (launch pricing, DES 18-10-2026).
 *
 * @details
 * Every figure comes from GET /billing/subscriptions `catalog[]` (proposed
 * fields: notesMinutes, includedMinutes, pooled, perSeat, minSeats,
 * overageRateMinor, noPlanMinChargeMinor, s2sRateMinor, historyDays,
 * askLimit, team). A row shows only when at least one plan sends its field,
 * so today's catalogue gives a short table and nothing is invented.
 *
 * Pure (relative imports only) so node:test can load it.
 *
 * @author DES
 * @date 18-10-2026
 */

import { formatMinor, type BillingInterval } from "./planConfirm.ts";
import {
  FREE_POOL_LABEL,
  FREE_POOL_SUB,
  FROM_FREE_MINUTES,
  freeIntelValue,
  freePoolValue,
} from "../../shared/billing/freePool.ts";

export type ComparePlan = {
  code: string;
  name: string;
  currency: string;
  priceMinor?: number | null;
  annualPriceMinor?: number | null;
  perSeat?: boolean | null;
  minSeats?: number | null;
  notesMinutes?: number | null;
  notesFairUseMinutes?: number | null;
  unlimitedNotes?: boolean | null;
  includedMinutes?: number | null;
  pooled?: boolean | null;
  overageRateMinor?: number | null;
  noPlanMinChargeMinor?: number | null;
  s2sRateMinor?: number | null;
  historyDays?: number | null;
  askLimit?: number | null;
  team?: boolean | null;
  enterprise?: boolean | null;
  contactSales?: boolean | null;
  /** B43 (31-10-2026): Free's minutes are ONE pool for Meeting Notes or Full Intelligence, never charged. */
  onePool?: boolean | null;
  /** B43: most of the pool Full Intelligence may use (null / the pool = no cap). */
  intelMaxMinutes?: number | null;
  /** Per-session minimum (Free row, 21-10-2026); older rows send noPlanMinChargeMinor. */
  minChargeMinor?: number | null;
  annualSavingPct?: number | null;
  /** False on the Free row while FREE_PLAN_ENABLED is off; the column still shows. */
  available?: boolean | null;
  /** B20: Free "one_time" = the minutes and Asks are a total, once. */
  resetMode?: string | null;
  /** B20: one-time Free ends this many days after sign-up (null = never). */
  expiryDays?: number | null;
};

/** `sub`: a small line under the label (W34, Free's one pool). */
export type CompareRow = { label: string; cells: string[]; sub?: string };

const has = (plans: ComparePlan[], key: keyof ComparePlan) => plans.some((p) => p[key] !== undefined);

function money(minor: number | null | undefined, currency: string): string {
  return minor == null ? "—" : formatMinor(minor, currency);
}

/** A per-minute rate in pence / cents when under a unit, e.g. "1p / min", "1.2p / min", "$0.50 / min". */
export function perMinute(minor: number | null | undefined, currency: string): string {
  if (minor == null) return "—";
  const cur = (currency || "GBP").toUpperCase();
  if (minor < 100 && cur === "GBP") return `${Number(minor.toFixed(2))}p / min`;
  if (minor < 100 && (cur === "USD" || cur === "EUR")) return `${Number(minor.toFixed(2))}${cur === "USD" ? "¢" : " cent"} / min`;
  return `${formatMinor(minor, currency)} / min`;
}

/** Enterprise is contract-priced: the catalogue sends contactSales and null figures. */
const isContract = (p: ComparePlan) => !!(p.enterprise || p.contactSales);

export function priceCell(p: ComparePlan, interval: BillingInterval): string {
  if (isContract(p)) return "Custom";
  if (p.priceMinor === 0) return "Free";
  const v = interval === "year" ? p.annualPriceMinor : p.priceMinor;
  if (v == null) return interval === "year" ? "Monthly only" : "—";
  return `${money(v, p.currency)}${p.perSeat ? " per seat" : ""} / ${interval === "year" ? "year" : "month"}`;
}

function minutes(n: number): string {
  return `${n.toLocaleString("en-GB")} min`;
}

/**
 * The Compare plans rows (DES 21-10-2026). Only catalogue fields, in this
 * order: seats, Meeting Notes, Intelligence minutes, pooled, Ask, history,
 * interpretation, extra Intelligence. A row shows only when some plan sends
 * its field. Null on a self-serve plan means no cap (the server's rule for
 * notesMinutes, askLimit and historyDays); a contract plan reads "Custom".
 * Prices are in the column heads (priceCell), not a row.
 */
export function buildCompareRows(plans: ComparePlan[]): CompareRow[] {
  const rows: CompareRow[] = [];
  const cell = (fn: (p: ComparePlan) => string) => plans.map((p) => (isContract(p) ? "Custom" : fn(p)));
  // W34 (DES 31-10-2026): Free's minutes are one pool for everything (B43),
  // so the pool is the first row and each product says where its minutes
  // come from.
  const onePool = plans.some((p) => p.onePool);
  if (onePool) {
    rows.push({
      label: FREE_POOL_LABEL,
      sub: FREE_POOL_SUB,
      cells: cell((p) => (p.onePool ? (freePoolValue(p.notesMinutes, p.resetMode === "one_time") ?? "—") : "—")),
    });
  }

  if (has(plans, "perSeat") || has(plans, "minSeats")) {
    rows.push({
      label: "Seats",
      cells: cell((p) => (p.perSeat ? `Per seat${p.minSeats && p.minSeats > 1 ? `, ${p.minSeats} minimum` : ""}` : "1 person")),
    });
  }
  if (has(plans, "notesMinutes") || has(plans, "unlimitedNotes")) {
    rows.push({
      label: "Meeting Notes",
      cells: cell((p) => {
        if (p.onePool) return FROM_FREE_MINUTES;
        if (p.notesMinutes != null) return `${minutes(p.notesMinutes)}${p.resetMode === "one_time" ? ", once" : " / month"}`;
        if (p.notesMinutes === null || p.unlimitedNotes) {
          return p.notesFairUseMinutes
            ? `Unlimited (fair use ${Math.round(p.notesFairUseMinutes / 60)} h${p.perSeat ? " per seat" : ""} / month)`
            : "Unlimited";
        }
        return "—";
      }),
    });
  }
  if (has(plans, "includedMinutes")) {
    rows.push({
      label: "Intelligence minutes",
      cells: cell((p) =>
        p.onePool
          ? freeIntelValue(p.intelMaxMinutes, p.notesMinutes)
          : p.includedMinutes
            ? `${minutes(p.includedMinutes)}${p.perSeat ? " per seat" : ""} / month`
            : "None included",
      ),
    });
  }
  if (onePool) {
    // B4: voice notes are never charged on a plan either (counted only).
    rows.push({ label: "Voice notes", cells: cell((p) => (p.onePool ? FROM_FREE_MINUTES : "Included")) });
  }
  if (has(plans, "pooled")) {
    rows.push({ label: "Shared by the team", cells: cell((p) => (p.pooled ? "Yes" : "No")) });
  }
  if (has(plans, "askLimit")) {
    rows.push({
      label: "Ask AI",
      cells: cell((p) =>
        p.askLimit != null
          ? `${p.askLimit.toLocaleString("en-GB")}${p.resetMode === "one_time" ? ", once" : " / month"}`
          : "Unlimited",
      ),
    });
  }
  if (has(plans, "historyDays")) {
    rows.push({ label: "History", cells: cell((p) => (p.historyDays != null ? `${p.historyDays} days` : "Unlimited")) });
  }
  if (has(plans, "s2sRateMinor")) {
    rows.push({ label: "Live interpretation", cells: cell((p) => perMinute(p.s2sRateMinor, p.currency)) });
  }
  if (has(plans, "overageRateMinor")) {
    rows.push({
      label: "Extra Intelligence",
      cells: cell((p) => {
        if (p.overageRateMinor == null) return "—";
        if (p.onePool) return "Never charged"; // B43: no pay as you go on Free
        const minCharge = p.minChargeMinor ?? p.noPlanMinChargeMinor;
        return `${perMinute(p.overageRateMinor, p.currency)}${minCharge ? `, ${money(minCharge, p.currency)} minimum per session` : ""}`;
      }),
    });
  }
  return rows;
}

/** The columns each audience sees (DES 21-10-2026): their own self-serve plan, Free and Enterprise. */
const AUDIENCE_CODES = {
  personal: ["free", "pro", "enterprise"],
  company_admin: ["free", "business", "enterprise"],
} as const;

/**
 * The table's columns from GET /billing/subscriptions/catalog: `compare`
 * (= [free, ...catalog], BACKEND 2975ff4) is the source; Free always shows
 * (DES 21-10-2026). `catalog` stays the list of buyable plans and is only a
 * fallback for a backend without `compare` (no Free column then).
 */
export function comparePlansFor(
  data: { catalog?: ComparePlan[] | null; compare?: ComparePlan[] | null } | null | undefined,
  audience: keyof typeof AUDIENCE_CODES,
): ComparePlan[] {
  const rows = data?.compare ?? data?.catalog ?? [];
  const codes: readonly string[] = AUDIENCE_CODES[audience];
  return codes
    .map((c) => rows.find((p) => p.code === c))
    .filter((p): p is ComparePlan => !!p);
}

/**
 * Full Intelligence over the allowance so far this period and what it adds to
 * the next invoice. BACKEND ADR-045 sends no overage field yet: minutes =
 * max(0, used - included), cents = minutes × the plan's overageRateMinor.
 */
export function overageSoFar(
  pool: { used: number; included: number; overageMinutes?: number | null; overageCents?: number | null } | null | undefined,
  overageRateMinor: number | null | undefined,
): { minutes: number; cents: number | null } | null {
  if (!pool) return null;
  const minutes = pool.overageMinutes ?? Math.max(0, Math.floor(pool.used) - Math.floor(pool.included));
  if (minutes <= 0) return null;
  const cents =
    pool.overageCents ?? (overageRateMinor != null && Number.isFinite(overageRateMinor) ? Math.round(minutes * overageRateMinor) : null);
  return { minutes, cents };
}

/**
 * The Free tier as a plan card / comparison column. The server sends it as
 * `freePlan` (only for a user on Free, while FREE_PLAN_ENABLED is on), not in
 * the catalogue; its rates are the no-plan (basic) rates.
 */
export function freePlanRow(
  freePlan:
    | {
        notesMinutes?: number | null;
        askLimit?: number | null;
        historyDays?: number | null;
        resetMode?: string | null;
        /** B43: present = one pool for Notes or Full Intelligence (never charged). */
        poolMinutesLeft?: number | null;
        intelMaxMinutes?: number | null;
      }
    | null
    | undefined,
  basic: { currency?: string | null; boardroomRateMinor?: number | null; s2sRateMinor?: number | null } | null | undefined,
  currency: string,
): ComparePlan | null {
  if (!freePlan) return null;
  return {
    code: "free",
    name: "Free",
    currency: basic?.currency || currency,
    priceMinor: 0,
    annualPriceMinor: 0,
    notesMinutes: freePlan.notesMinutes ?? null,
    includedMinutes: 0,
    overageRateMinor: basic?.boardroomRateMinor ?? null,
    s2sRateMinor: basic?.s2sRateMinor ?? null,
    historyDays: freePlan.historyDays ?? null,
    askLimit: freePlan.askLimit ?? null,
    resetMode: freePlan.resetMode ?? null,
    onePool: freePlan.poolMinutesLeft != null,
    intelMaxMinutes: freePlan.intelMaxMinutes ?? null,
    team: false,
  };
}
